import { getCurrencySymbol } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { InputNumber } from 'primeng/inputnumber';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ItemsService } from '../../../../../../services/financeiro/item.service';
import { MOEDA_CONFIG, Moeda, MoedaConfig } from '../../../../../../enums/moeda.enum';
import { TipoItem } from '../../../../../../enums/TipoItem.enum';

/** Linha escolhida no popup, pronta para virar item do carrinho de venda. */
export interface ItemVendaSelecionado {
  item: any;
  quantidade: number;
  valorUnitario: number;
}

/**
 * Popup "Seleccionar Artículo" para a VENDA (aba Nova venda do Operar Cliente).
 *
 * Espelha o layout do popup da compra (busca + tabela paginada de itens), mas com
 * campos de VENDA: quantidade + preço de venda (default = preço do item). NÃO tem
 * campos de custo/compra. Busca os itens do CLIENTE sob contexto (`/item` e
 * `/item/buscar` — X-Empresa-Contexto injetado pelo interceptor).
 *
 * Ao confirmar, emite [selecionado] com { item, quantidade, valorUnitario } e o
 * pai adiciona a linha ao carrinho.
 */
@Component({
  selector: 'app-operar-venda-item-selector',
  templateUrl: './operar-venda-item-selector.component.html',
  styleUrls: ['./operar-venda-item-selector.component.scss'],
})
export class OperarVendaItemSelectorComponent implements OnInit, OnDestroy {
  /** Config de moeda do cliente (símbolo/casas/locale) para formatar os preços. */
  @Input() cfgMoeda: MoedaConfig = MOEDA_CONFIG[Moeda.GUARANI];

  /**
   * Quando true, o popup lista apenas MERCADORIAS com saldo (> 0). Usado pelo
   * Criar Pedido. Default false preserva o comportamento da venda do contador.
   */
  @Input() somenteMercadoriaComSaldo = false;

  /** Emite false quando o popup deve fechar. */
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Emite a linha escolhida (item + quantidade + preço de venda). */
  @Output() selecionado = new EventEmitter<ItemVendaSelecionado>();

  /** Campo de preço — só existe quando o item não tem preço cadastrado. */
  @ViewChild('inputPreco') inputPreco?: InputNumber;

  visivel = true;

  // --- Tabela / busca ---
  itens: any[] = [];
  loading = false;
  totalRecords = 0;
  first = 0;
  rows = 10;
  private sort = 'nome,asc';
  busca = '';
  private buscaSubject = new Subject<void>();

  /** Colunas que existem de fato na entidade (evita erro de path no backend). */
  private readonly camposOrdenaveis = ['codigoItem', 'nome', 'saldo', 'precoDeVenda'];

  /** Opcoes de "itens por pagina" — as mesmas do paginador embutido anterior. */
  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  /** Valor anterior de `rows`, usado como guarda no onRowsPerPageChange: o
   *  (ngModelChange) do dropdown dispara no bootstrap com o valor atual e
   *  recarregaria a lista sem necessidade. Mesmo padrao da aba "Listagem de
   *  notas" (operar-cliente.component.ts). */
  private lastRows = 10;

  // --- Seleção / campos de venda ---
  itemSelecionado: any = null;
  quantidade = 1;
  precoVenda = 0;

  constructor(private itemService: ItemsService) {}

  ngOnInit(): void {
    // Reaplica a busca com debounce a cada digitação.
    this.buscaSubject.pipe(debounceTime(350)).subscribe(() => {
      this.first = 0;
      this.carregar();
    });
    this.carregar();
  }

  ngOnDestroy(): void {
    this.buscaSubject.complete();
  }

  get decimais(): number {
    return this.cfgMoeda.symbol === 'PYG' ? 0 : 2;
  }

  /**
   * Simbolo da moeda do cliente (ex.: "Gs." para PYG em es-PY). Usa a mesma
   * fonte do `moedaSimbolo` da tabela (CurrencyPipe com display 'symbol'), para
   * o campo e a coluna "Preco venda" nunca divergirem.
   */
  get simboloMoeda(): string {
    return getCurrencySymbol(this.cfgMoeda.symbol, 'wide', this.cfgMoeda.locale);
  }

  /**
   * Prefixo exibido dentro do campo de preco.
   *
   * ATENCAO: NAO trocar por `mode="currency"` no p-inputNumber — com PYG o modo
   * currency bloqueia a digitacao (armadilha ja documentada no projeto). O par
   * `mode="decimal"` + `[prefix]` + `[locale]` mostra a moeda mantendo o campo
   * digitavel.
   */
  get prefixoMoeda(): string {
    return `${this.simboloMoeda} `;
  }

  /** Subtotal ao vivo (quantidade x preco) exibido antes de adicionar ao carrinho. */
  get subtotal(): number {
    const qtd = Number(this.quantidade) || 0;
    const preco = Number(this.precoVenda) || 0;
    return qtd * preco;
  }

  /**
   * O preco so pode ser digitado quando o item NAO tem preco cadastrado
   * (`precoDeVenda` <= 0). Com preco cadastrado o campo e somente-leitura: quem
   * opera a venda pelo contador nao arbitra o preco do item do cliente.
   *
   * Mesma regra do "dialog de preco zero" da pagina /vendas
   * (vendas.component.ts -> adicionarProduto): la o vendedor tambem so informa
   * preco quando o item esta sem preco. O tipo do item (MERCADORIA/SERVICO) NAO
   * entra na condicao — servico com preco cadastrado tambem fica fixo.
   */
  get precoEditavel(): boolean {
    if (!this.itemSelecionado) return false;
    return Number(this.itemSelecionado.precoDeVenda ?? 0) <= 0;
  }

  /**
   * Item sem preco cadastrado e sem preco digitado. Bloqueia a selecao: sem
   * isto a linha entra no carrinho por 0 e a nota fiscal sai com total zero.
   */
  get precoInvalido(): boolean {
    return this.precoEditavel && (Number(this.precoVenda) || 0) <= 0;
  }

  /**
   * Saldo em estoque do item selecionado. Retorna null quando nao ha item ou
   * quando ele e SERVICO — servico nao controla estoque, entao avisar sobre
   * saldo nesse caso seria ruido.
   */
  get saldoDisponivel(): number | null {
    if (!this.itemSelecionado) return null;
    const tipo = this.itemSelecionado.tipoItem?.enum ?? this.itemSelecionado.tipoItem;
    if (tipo === TipoItem.SERVICO) return null;
    const saldo = Number(this.itemSelecionado.saldo);
    return isNaN(saldo) ? null : saldo;
  }

  /**
   * Quantidade acima do saldo em estoque. E apenas um AVISO — o sistema permite
   * saldo negativo, entao isto nao desabilita o botao de confirmar.
   */
  get excedeSaldo(): boolean {
    const saldo = this.saldoDisponivel;
    return saldo !== null && (Number(this.quantidade) || 0) > saldo;
  }

  // ===========================================================================
  // Busca
  // ===========================================================================

  onBuscaChange(): void {
    this.buscaSubject.next();
  }

  /**
   * Tabela `[lazy]`: clicar num cabecalho `pSortableColumn` dispara o proprio
   * onLazyLoad ja com sortField/sortOrder — por isso a ORDENACAO e capturada
   * aqui, sem um `(onSort)` separado que causaria recarga dupla.
   *
   * Desde a migracao para o `<p-paginator>` externo, este handler trata SOMENTE
   * ordenacao: pagina e "itens por pagina" chegam por onPageChange /
   * onRowsPerPageChange. O early-return quando o sort nao mudou e o que impede
   * carga dupla — o p-table reemite onLazyLoad a cada mudanca de `[value]`,
   * `[first]` ou `[rows]` (inclusive no init e apos cada busca).
   */
  onLazyLoad(event: any): void {
    const campo =
      typeof event.sortField === 'string' && this.camposOrdenaveis.includes(event.sortField)
        ? event.sortField
        : null;
    // Sem campo utilizavel, mantem a ordenacao atual (comportamento anterior).
    const novoSort = campo
      ? `${campo},${event.sortOrder === -1 ? 'desc' : 'asc'}`
      : this.sort;

    if (novoSort === this.sort) return;

    this.sort = novoSort;
    this.first = 0; // trocar a ordenacao volta para a primeira pagina
    this.carregar();
  }

  /** Troca de pagina pelo paginador externo (mesmo modelo da "Listagem de notas").
   *  Como a tabela e lazy, recarregamos a pagina solicitada do backend. */
  onPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.rows;
    const page = event.page ?? 0;
    this.rows = size;
    this.lastRows = size;
    this.first = page * size;
    this.carregar();
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega. */
  onRowsPerPageChange(): void {
    if (this.rows === this.lastRows) return;
    this.lastRows = this.rows;
    this.first = 0;
    this.carregar();
  }

  private carregar(): void {
    this.loading = true;
    const page = Math.floor(this.first / this.rows);
    const termo = this.busca.trim();

    // Filtro opcional (Criar Pedido): apenas mercadorias com saldo.
    const filtros = this.somenteMercadoriaComSaldo
      ? { apenasComSaldo: true, apenasMercadoria: true }
      : {};

    const req = termo
      ? this.itemService.get('/item/buscar', {
          page,
          size: this.rows,
          sort: this.sort,
          filter: `nome=${termo}&barras=${termo}&codigoItem=${termo}&nomeGrupo=${termo}&nomeSubGrupo=${termo}`,
          ...filtros,
        })
      : this.itemService.get('/item', { page, size: this.rows, sort: this.sort, ...filtros });

    req.subscribe({
      next: (res: any) => {
        this.itens = res?.content ?? [];
        this.totalRecords = res?.totalElements ?? 0;
        this.loading = false;
      },
      error: () => {
        // 404 (sem itens) e erros caem em lista vazia — não quebram o popup.
        this.itens = [];
        this.totalRecords = 0;
        this.loading = false;
      },
    });
  }

  // ===========================================================================
  // Seleção
  // ===========================================================================

  onRowSelect(item: any): void {
    this.selecionarLinha(item);
  }

  selecionarLinha(item: any): void {
    this.itemSelecionado = item;
    this.precoVenda = Number(item?.precoDeVenda ?? 0);
    this.quantidade = 1;

    // Item sem preço: o cursor já vai para o campo, como no diálogo "Definir
    // preço de venda" da página /vendas. O setTimeout espera o *ngIf renderizar.
    if (this.precoEditavel) {
      setTimeout(() => this.inputPreco?.input?.nativeElement?.focus(), 0);
    }
  }

  /** Duplo clique confirma direto. */
  onRowDblClick(item: any): void {
    this.selecionarLinha(item);
    this.confirmar();
  }

  confirmar(): void {
    if (!this.itemSelecionado) return;
    // Guarda tambem o duplo clique na linha (onRowDblClick), que nao passa pelo
    // botao desabilitado do rodape.
    if (this.precoInvalido) return;
    this.selecionado.emit({
      item: this.itemSelecionado,
      quantidade: this.quantidade > 0 ? this.quantidade : 1,
      valorUnitario: this.precoVenda >= 0 ? this.precoVenda : 0,
    });
    this.fechar();
  }

  cancelar(): void {
    this.fechar();
  }

  fechar(): void {
    this.visivel = false;
    this.visibleChange.emit(false);
  }
}
